import { Component } from '@theme/component';
import {
  canWarm,
  playableSource,
  releaseVideo,
  warmMedia,
  whenIdleAfterLoad,
} from '@theme/video-loading';
import { DialogOpenEvent } from '@theme/dialog';
import { connect as connectUpsellCards } from '@theme/product-upsell-card';

/**
 * UGC video reels — enhances blocks/ugc-video-reels.liquid.
 *
 * Liquid renders the cards, posters and the dialog shell; this module adds the
 * muted previews and the lightbox. Nothing video-sized loads until it is
 * needed: only the card that is previewing has a source attached (released
 * again when it stops), and each card's player markup sits in an inert
 * <template> that is cloned into the dialog only when that video is opened.
 *
 * The lightbox is a vertical feed with one view per video. Views are built
 * once, on first open, and show a poster; only the view in the middle of the
 * screen gets its video, and product cards render for that view and its
 * neighbours so nothing pops in while scrolling.
 */

const PREVIEW_SECONDS = 6;
const SWIPE_SETTLE_MS = 180;
const WARM_DELAY_MS = 600;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @typedef {Object} UgcReelsRefs
 * @property {HTMLElement} [feed] - Scrolling feed inside the dialog
 */

/**
 * @extends {Component<UgcReelsRefs>}
 */
class UgcVideoReels extends Component {
  /** @type {HTMLElement[]} */
  #items = [];

  /** @type {HTMLElement[]} */
  #views = [];

  /** @type {Set<HTMLElement>} */
  #visible = new Set();

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #warmController = null;

  /** @type {HTMLElement | null} */
  #activePreview = null;

  /** @type {HTMLElement | null} */
  #pointerItem = null;

  #previewsReady = false;
  #index = -1;
  #direction = 1;
  #lastFeedScroll = 0;
  #videoTimer = 0;
  #warmTimer = 0;

  /** @type {WeakSet<HTMLElement>} */
  #warmed = new WeakSet();

  /** @type {Element[]} */
  #mounted = [];

  connectedCallback() {
    super.connectedCallback();

    this.#items = [...this.querySelectorAll('[data-ugc-item]')];
    this.dialogHost = this.querySelector('[data-ugc-dialog-host]');
    this.dialog = this.querySelector('[data-ugc-dialog]');
    this.feed = this.querySelector('[data-ugc-feed]');
    this.viewTemplate = this.querySelector('[data-ugc-view-template]');
    this.status = this.querySelector('[data-ugc-status]');
    this.previewMode = this.dataset.preview || 'off';
    this.autoplayNext = this.dataset.autoplayNext === 'true';

    this.#connectTriggers();
    this.#connectDialog();
    this.#connectPreviews();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#teardownVideo();
    this.#stopPreview();
    this.#cancelWarm();
    this.#feedObserver?.disconnect();
    this.#previewObserver?.disconnect();
    window.clearTimeout(this.#videoTimer);
  }

  /** @type {IntersectionObserver | null} */
  #feedObserver = null;

  /** @type {IntersectionObserver | null} */
  #previewObserver = null;

  // ===========================================================================
  // Rail
  // ===========================================================================

  #connectTriggers() {
    const { signal } = this.#controller;

    for (const [index, item] of this.#items.entries()) {
      const trigger = item.querySelector('[data-ugc-open]');
      if (!(trigger instanceof HTMLButtonElement)) continue;

      trigger.disabled = false;
      if (this.dialog) {
        trigger.setAttribute('aria-controls', this.dialog.id);
        trigger.setAttribute('aria-expanded', 'false');
      }
      trigger.addEventListener('click', () => this.open(index), { signal });
    }
  }

  // ===========================================================================
  // Previews — one at a time, and only that card holds a source
  // ===========================================================================

  #previewsAllowed() {
    return this.previewMode !== 'off' && !reducedMotion.matches && canWarm();
  }

  async #connectPreviews() {
    if (this.previewMode === 'off') return;
    const { signal } = this.#controller;

    for (const item of this.#items) {
      const preview = item.querySelector('[data-ugc-preview]');
      if (!(preview instanceof HTMLVideoElement)) continue;

      preview.addEventListener('timeupdate', () => {
        if (preview.currentTime > PREVIEW_SECONDS) preview.currentTime = 0;
      }, { signal });
      preview.addEventListener('playing', () => item.setAttribute('data-previewing', ''), { signal });
      preview.addEventListener('pause', () => item.removeAttribute('data-previewing'), { signal });

      const enter = () => {
        this.#pointerItem = /** @type {HTMLElement} */ (item);
        this.#schedulePreview();
      };
      const leave = () => {
        if (this.#pointerItem !== item) return;
        this.#pointerItem = null;
        this.#schedulePreview();
      };

      item.addEventListener('pointerenter', enter, { signal });
      item.addEventListener('pointerleave', leave, { signal });
      item.addEventListener('focusin', enter, { signal });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(/** @type {Node} */ (event.relatedTarget))) leave();
      }, { signal });
    }

    reducedMotion.addEventListener('change', () => this.#schedulePreview(), { signal });
    document.addEventListener('visibilitychange', () => this.#schedulePreview(), { signal });

    this.#previewObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#visible.add(/** @type {HTMLElement} */ (entry.target));
        else this.#visible.delete(/** @type {HTMLElement} */ (entry.target));
      }
      this.#schedulePreview();
    }, { threshold: 0.75 });

    for (const item of this.#items) {
      if (item.querySelector('[data-ugc-preview]')) this.#previewObserver.observe(item);
    }

    // Previews wait for the page to finish loading and then for an idle moment,
    // so they never compete with the page's own images, fonts and scripts.
    await whenIdleAfterLoad();
    if (this.#controller.signal.aborted) return;

    this.#previewsReady = true;
    this.#schedulePreview();
  }

  #previewFrame = 0;

  #schedulePreview() {
    cancelAnimationFrame(this.#previewFrame);
    this.#previewFrame = requestAnimationFrame(() => this.#updatePreview());
  }

  #choosePreview() {
    if (!this.#previewsReady || !this.#previewsAllowed()) return null;
    if (document.hidden || this.dialog?.open) return null;
    if (this.#pointerItem?.querySelector('[data-ugc-preview]')) return this.#pointerItem;
    if (this.previewMode !== 'in_view') return null;
    return this.#items.find((item) => this.#visible.has(item)) ?? null;
  }

  #updatePreview() {
    const next = this.#choosePreview();
    if (next === this.#activePreview) return;

    this.#stopPreview();
    if (next) this.#startPreview(next);
  }

  /** @param {HTMLElement} item */
  #startPreview(item) {
    const preview = item.querySelector('[data-ugc-preview]');
    if (!(preview instanceof HTMLVideoElement)) return;

    this.#activePreview = item;
    if (!preview.getAttribute('src') && preview.dataset.src) preview.src = preview.dataset.src;
    preview.muted = true;
    preview.play().catch(() => {});
  }

  #stopPreview() {
    const item = this.#activePreview;
    this.#activePreview = null;
    const preview = item?.querySelector('[data-ugc-preview]');
    if (!(preview instanceof HTMLVideoElement)) return;

    preview.pause();
    // Release rather than merely pause, so a card that is not previewing holds
    // no decoded frames or buffer. The URL is parked back in data-src.
    releaseVideo(preview);
  }

  // ===========================================================================
  // Dialog
  // ===========================================================================

  #connectDialog() {
    if (!this.dialog) return;
    const { signal } = this.#controller;

    // A flick through the feed passes several views. Starting a stream for each
    // one leaves Safari finishing abandoned loads before the video the shopper
    // lands on, so a view only gets its player once the feed has stopped.
    this.feed?.addEventListener('scroll', () => {
      this.#lastFeedScroll = performance.now();
    }, { passive: true, signal });

    this.dialog.addEventListener('close', () => this.#onClose(), { signal });

    this.dialog.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('[data-ugc-review-toggle]')) {
        const review = target.closest('[data-ugc-review]');
        if (review) this.#toggleReview(review);
        return;
      }

      // Adding to cart opens the cart drawer; close first so it is not hidden
      // behind the modal. The form stays in the closed dialog, so the cart's
      // submit listener still sees it.
      if (target.closest('form[action*="/cart/add"] [type="submit"]')) {
        this.#close();
        return;
      }

      const surface = target.closest(
        '.ugc-reels__player, .ugc-reels__view-info, .ugc-reels__steps, .ugc-reels__close'
      );
      if (!surface) this.#close();
    }, { signal });

    for (const button of this.dialog.querySelectorAll('[data-ugc-step]')) {
      button.addEventListener('click', () => {
        this.#step(Number(button.getAttribute('data-ugc-step')));
      }, { signal });
    }

    this.dialog.addEventListener('keydown', (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;

      const target = event.target;
      if (target instanceof Element && target.matches('input, select, textarea, video')) return;

      if (event.key === 'ArrowUp' || event.key === 'PageUp') {
        event.preventDefault();
        this.#step(-1);
      } else if (event.key === 'ArrowDown' || event.key === 'PageDown') {
        event.preventDefault();
        this.#step(1);
      }
    }, { signal });
  }

  /** @param {Element} review */
  #toggleReview(review) {
    const expanded = review.toggleAttribute('data-expanded');
    const more = review.querySelector('[data-ugc-review-more]');
    const less = review.querySelector('[data-ugc-review-less]');
    if (more instanceof HTMLElement) more.hidden = expanded;
    if (less instanceof HTMLElement) less.hidden = !expanded;

    requestAnimationFrame(() => {
      const media = review.closest('[data-ugc-view]')?.querySelector('deferred-media');
      media?.positionCaptions?.();
    });
  }

  #buildViews() {
    if (this.#views.length || !this.feed || !this.viewTemplate) return;

    const shell = this.viewTemplate.content.firstElementChild;
    if (!shell) return;

    this.#views = this.#items.map((item, index) => {
      const view = /** @type {HTMLElement} */ (shell.cloneNode(true));
      view.dataset.index = String(index);
      view.inert = true;

      const poster = view.querySelector('[data-ugc-view-poster]');
      const url = this.#posterFor(item);
      if (poster instanceof HTMLImageElement && url) poster.src = url;
      else poster?.remove();

      this.feed?.append(view);
      return view;
    });

    this.#feedObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#activate(Number(/** @type {HTMLElement} */ (entry.target).dataset.index));
        }
      }
    }, { root: this.feed, threshold: 0.6 });

    for (const view of this.#views) this.#feedObserver.observe(view);
  }

  /**
   * The card's own poster is already in the browser cache, so a view opens on a
   * frame at once.
   * @param {HTMLElement} item
   * @returns {string}
   */
  #posterFor(item) {
    const card = item.querySelector('.ugc-reels__poster');
    if (card instanceof HTMLImageElement && card.complete) return card.currentSrc || card.src;

    const template = item.querySelector('[data-ugc-template]');
    const video = template instanceof HTMLTemplateElement
      ? template.content.querySelector('video[poster]')
      : null;
    return video?.getAttribute('poster') ?? '';
  }

  /**
   * Cloned details repeat ids across views, so every id, label target and radio
   * group gets a per-view suffix.
   * @param {DocumentFragment} root
   * @param {string} suffix
   */
  #uniquify(root, suffix) {
    for (const element of root.querySelectorAll('[id]')) {
      element.id = `${element.id}${suffix}`;
    }
    for (const element of root.querySelectorAll('[for]')) {
      element.setAttribute('for', `${element.getAttribute('for')}${suffix}`);
    }
    for (const element of root.querySelectorAll('[aria-controls]')) {
      element.setAttribute('aria-controls', `${element.getAttribute('aria-controls')}${suffix}`);
    }
    for (const element of root.querySelectorAll('input[type="radio"][name]')) {
      const input = /** @type {HTMLInputElement} */ (element);
      input.name = `${input.name}${suffix}`;
    }
  }

  /** @param {number} index */
  #renderDetails(index) {
    const view = this.#views[index];
    const item = this.#items[index];
    if (!view || !item || view.hasAttribute('data-details-ready')) return;
    view.setAttribute('data-details-ready', '');

    const info = view.querySelector('[data-ugc-view-info]');
    const template = item.querySelector('[data-ugc-template]');
    if (!info || !(template instanceof HTMLTemplateElement)) return;

    const details = template.content.querySelector('[data-ugc-part="details"]');
    const fragment = document.createDocumentFragment();
    if (details) fragment.append(...details.cloneNode(true).childNodes);

    this.#uniquify(fragment, `-v${index}`);
    info.replaceChildren(fragment);

    this.#connectProductTrack(info);
    this.#connectReviewClamp(info);
    connectUpsellCards(info);
  }

  /** @param {Element} root */
  #connectReviewClamp(root) {
    const text = root.querySelector('[data-ugc-review-text]');
    const toggle = root.querySelector('[data-ugc-review-toggle]');
    if (!(text instanceof HTMLElement) || !(toggle instanceof HTMLElement)) return;

    // Only offer "See more" when the copy is actually clamped. A view that has
    // not been laid out yet reports zero height; leave the toggle visible in
    // that case rather than hiding copy the shopper cannot reach.
    toggle.hidden = text.clientHeight > 0 && text.scrollHeight <= text.clientHeight + 1;
  }

  /** @param {Element} root */
  #connectProductTrack(root) {
    const track = root.querySelector('[data-ugc-product-track]');
    if (!(track instanceof HTMLElement)) return;

    const slides = [...track.querySelectorAll('.ugc-reels__product-slide')];
    if (slides.length < 2) return;

    const { signal } = this.#controller;
    let index = 0;

    const offsetOf = (slide) => slide.offsetLeft - slides[0].offsetLeft;

    const sync = () => {
      let nearest = Infinity;
      for (const [i, slide] of slides.entries()) {
        const distance = Math.abs(offsetOf(slide) - track.scrollLeft);
        if (distance < nearest) {
          nearest = distance;
          index = i;
        }
      }
    };

    const step = (direction) => {
      index = (index + direction + slides.length) % slides.length;
      track.scrollTo({
        left: offsetOf(slides[index]),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    };

    for (const button of root.querySelectorAll('[data-ugc-product-step]')) {
      button.addEventListener('click', () => {
        step(Number(button.getAttribute('data-ugc-product-step')));
      }, { signal });
    }

    track.addEventListener('scroll', sync, { passive: true, signal });
    sync();
  }

  // ===========================================================================
  // Player — the theme's <deferred-media>
  // ===========================================================================

  /**
   * Mounts the card's player into the view. The markup is the theme's own
   * `video` snippet, so once it is connected the mute, play and captions
   * buttons, the progress bar and the lazy source handling are all the
   * theme's — this only has to start it.
   * @param {number} index
   */
  #renderVideo(index) {
    const view = this.#views[index];
    const item = this.#items[index];
    const template = item?.querySelector('[data-ugc-template]');
    if (!view || !(template instanceof HTMLTemplateElement)) return;

    const media = template.content.querySelector('[data-ugc-part="media"]');
    const player = view.querySelector('[data-ugc-player]');
    if (!media || !player) return;

    const nodes = [...media.cloneNode(true).childNodes];
    player.append(...nodes);
    this.#mounted = nodes.filter((node) => node instanceof Element);
    this.#player = /** @type {HTMLElement} */ (player);

    const deferred = /** @type {any} */ (player.querySelector('deferred-media'));
    this.#media = deferred ?? null;

    // Custom elements upgrade asynchronously after insertion, so wait a frame
    // before asking the component to play.
    requestAnimationFrame(() => {
      if (this.#index !== index || !this.#media) return;
      this.#media.playMedia?.();
      this.#media.unmuteAndRestart?.();
      this.#scheduleWarm(index);

      const video = this.#media.querySelector('video');
      if (video) {
        video.addEventListener('ended', () => this.#onEnded(), { signal: this.#controller.signal });
      }
    });
  }

  /** @type {HTMLElement | null} */
  #player = null;

  /** @type {any} */
  #media = null;

  #teardownVideo() {
    window.clearTimeout(this.#videoTimer);

    const media = this.#media;
    this.#media = null;
    if (media) {
      media.pauseMedia?.();
      releaseVideo(media.querySelector('video'));
    }

    for (const node of this.#mounted) node.remove();
    this.#mounted = [];
    this.#player = null;
  }


  // ===========================================================================
  // Feed navigation
  // ===========================================================================

  /** @param {number} index */
  #activate(index) {
    const view = this.#views[index];
    if (!view || index === this.#index) return;

    this.#teardownVideo();

    const previous = this.#views[this.#index];
    if (previous) previous.inert = true;

    if (this.#index >= 0) this.#direction = index > this.#index ? 1 : -1;
    this.#cancelWarm();
    this.#index = index;
    view.inert = false;

    for (const position of [index, index + 1, index - 1]) this.#renderDetails(position);
    for (const position of [index + 1, index - 1]) {
      const poster = this.#views[position]?.querySelector('[data-ugc-view-poster]');
      if (poster instanceof HTMLImageElement) poster.loading = 'eager';
    }

    this.#queueVideo(index);
    this.#syncSteps();
    this.#announce();
  }

  /** @param {number} index */
  #queueVideo(index) {
    window.clearTimeout(this.#videoTimer);
    const wait = SWIPE_SETTLE_MS - (performance.now() - this.#lastFeedScroll);

    if (wait <= 0) {
      this.#renderVideo(index);
      return;
    }

    this.#videoTimer = window.setTimeout(() => {
      if (this.#index === index && this.dialog?.open) this.#queueVideo(index);
    }, wait);
  }

  /**
   * Once the current video plays, pull the start of the next one in the
   * direction of travel into the HTTP cache the player reads from.
   * @param {number} index
   */
  #scheduleWarm(index) {
    window.clearTimeout(this.#warmTimer);
    if (!canWarm()) return;

    this.#warmTimer = window.setTimeout(() => {
      if (this.#index !== index || !this.dialog?.open) return;

      const item = this.#items[index + this.#direction];
      if (!item || this.#warmed.has(item)) return;

      const source = playableSource(item.querySelector('[data-ugc-template]'));
      if (!source) return;

      this.#warmController = new AbortController();
      warmMedia(source, this.#warmController.signal).then((done) => {
        if (done) this.#warmed.add(item);
      });
    }, WARM_DELAY_MS);
  }

  #cancelWarm() {
    window.clearTimeout(this.#warmTimer);
    this.#warmController?.abort();
    this.#warmController = null;
  }

  /** @param {number} index */
  open(index) {
    if (!this.dialog || !this.dialogHost) return;

    this.#stopPreview();
    this.#buildViews();

    // DialogComponent opens on a rAF, so the feed has no layout until then —
    // scrolling before that lands on 0 and the wrong video plays.
    if (this.dialog.open) {
      this.#jumpTo(index);
      return;
    }

    this.dialogHost.addEventListener(
      DialogOpenEvent.eventName,
      () => this.#jumpTo(index),
      { once: true, signal: this.#controller.signal }
    );
    /** @type {any} */ (this.dialogHost).showDialog?.();
  }

  /** @param {number} index */
  #jumpTo(index) {
    const view = this.#views[index];
    if (!view || !this.feed) return;

    this.feed.scrollTop = view.offsetTop;
    this.#index = -1;
    this.#activate(index);
  }

  #close() {
    /** @type {any} */ (this.dialogHost)?.closeDialog?.();
  }

  /** @param {number} direction */
  #step(direction) {
    const target = this.#views[this.#index + direction];
    if (!target) return;

    target.scrollIntoView({
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  #onClose() {
    this.#teardownVideo();
    this.#cancelWarm();

    const view = this.#views[this.#index];
    if (view) view.inert = true;
    this.#index = -1;

    this.#schedulePreview();
  }

  #onEnded() {
    if (this.autoplayNext && this.dialog?.open) this.#step(1);
  }

  #syncSteps() {
    const previous = this.dialog?.querySelector('[data-ugc-step="-1"]');
    const next = this.dialog?.querySelector('[data-ugc-step="1"]');
    previous?.setAttribute('aria-disabled', this.#index <= 0 ? 'true' : 'false');
    next?.setAttribute('aria-disabled', this.#index >= this.#items.length - 1 ? 'true' : 'false');
  }

  #announce() {
    if (!this.status || !this.dialog) return;

    const item = this.#items[this.#index];
    this.status.textContent = (this.dialog.getAttribute('data-status-template') ?? '')
      .replace('[index]', String(this.#index + 1))
      .replace('[total]', String(this.#items.length))
      .replace('[name]', item?.getAttribute('data-name') ?? '');
  }
}

if (!customElements.get('ugc-video-reels')) {
  customElements.define('ugc-video-reels', UgcVideoReels);
}
